/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const SET_HOOK_ALLOWLIST_DISCRIMINATOR = new Uint8Array([
  232, 28, 40, 143, 121, 182, 77, 103,
]);

export function getSetHookAllowlistDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_HOOK_ALLOWLIST_DISCRIMINATOR,
  );
}

export type SetHookAllowlistInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TAccountConfig extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      ...TRemainingAccounts,
    ]
  >;

export type SetHookAllowlistInstructionData = {
  discriminator: ReadonlyUint8Array;
  allowlist: Array<Address>;
};

export type SetHookAllowlistInstructionDataArgs = { allowlist: Array<Address> };

export function getSetHookAllowlistInstructionDataEncoder(): Encoder<SetHookAllowlistInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['allowlist', getArrayEncoder(getAddressEncoder())],
    ]),
    (value) => ({ ...value, discriminator: SET_HOOK_ALLOWLIST_DISCRIMINATOR }),
  );
}

export function getSetHookAllowlistInstructionDataDecoder(): Decoder<SetHookAllowlistInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['allowlist', getArrayDecoder(getAddressDecoder())],
  ]);
}

export function getSetHookAllowlistInstructionDataCodec(): Codec<
  SetHookAllowlistInstructionDataArgs,
  SetHookAllowlistInstructionData
> {
  return combineCodec(
    getSetHookAllowlistInstructionDataEncoder(),
    getSetHookAllowlistInstructionDataDecoder(),
  );
}

export type SetHookAllowlistAsyncInput<
  TAccountAdmin extends string = string,
  TAccountConfig extends string = string,
> = {
  admin: TransactionSigner<TAccountAdmin>;
  config?: Address<TAccountConfig>;
  allowlist: SetHookAllowlistInstructionDataArgs['allowlist'];
};

export async function getSetHookAllowlistInstructionAsync<
  TAccountAdmin extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: SetHookAllowlistAsyncInput<TAccountAdmin, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SetHookAllowlistInstruction<TProgramAddress, TAccountAdmin, TAccountConfig>
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    admin: { value: input.admin ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([99, 111, 110, 102, 105, 103])),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('config', accounts.config),
    ],
    data: getSetHookAllowlistInstructionDataEncoder().encode(
      args as SetHookAllowlistInstructionDataArgs,
    ),
    programAddress,
  } as SetHookAllowlistInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig
  >);
}

export type SetHookAllowlistInput<
  TAccountAdmin extends string = string,
  TAccountConfig extends string = string,
> = {
  admin: TransactionSigner<TAccountAdmin>;
  config: Address<TAccountConfig>;
  allowlist: SetHookAllowlistInstructionDataArgs['allowlist'];
};

export function getSetHookAllowlistInstruction<
  TAccountAdmin extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: SetHookAllowlistInput<TAccountAdmin, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): SetHookAllowlistInstruction<TProgramAddress, TAccountAdmin, TAccountConfig> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    admin: { value: input.admin ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('config', accounts.config),
    ],
    data: getSetHookAllowlistInstructionDataEncoder().encode(
      args as SetHookAllowlistInstructionDataArgs,
    ),
    programAddress,
  } as SetHookAllowlistInstruction<
    TProgramAddress,
    TAccountAdmin,
    TAccountConfig
  >);
}

export type ParsedSetHookAllowlistInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    admin: TAccountMetas[0];
    config: TAccountMetas[1];
  };
  data: SetHookAllowlistInstructionData;
};

export function parseSetHookAllowlistInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetHookAllowlistInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { admin: getNextAccount(), config: getNextAccount() },
    data: getSetHookAllowlistInstructionDataDecoder().decode(instruction.data),
  };
}
